import Vue from 'vue'; // Importáljuk a Vue-t a Vue.set használatához
import CONSTANTS from '@/constants';
import eventBus from "@/eventBus";
import { applyPeriodStartAt, collectReachedPeriods } from '@/utils/periodStartat';

// Ebben tároljuk azon játékosok ID-ját, akiknél épp fut a büntetés ellenőrzés.
const processingPenalties = new Set();
let dmrPushDebounceTimer = null;

// JAVÍTÁS (TELJESÍTMÉNY, 77. tétel): a `restoreCards` és a `restorePenalties`
// eseményenként hívta a `getPlayerById` gettert, ami MINDEN hívásnál újra
// összefűzte (flat) a négy játékos-/hivatalos-listát. Itt egyszer építünk egy
// id -> játékos indexet; az első találat nyer, pontosan úgy, mint a getter
// `find()`-ja (team_a_players, team_b_players, team_a_officials, team_b_officials
// sorrendben).
function buildPlayerIndex(gamedata) {
    const index = new Map();
    if (!gamedata) return index;
    [
        gamedata.team_a_players || [],
        gamedata.team_b_players || [],
        gamedata.team_a_officials || [],
        gamedata.team_b_officials || []
    ].forEach(list => {
        list.forEach(p => {
            if (p && !index.has(p.id)) index.set(p.id, p);
        });
    });
    return index;
}

// Egy játékos kiállítás-listájának "tartalmi aláírása" - a restorePenalties
// ez alapján dönti el, hogy a frissen kiszámolt lista ténylegesen eltér-e a
// meglévőtől. Az `isActive`/`finished`/`icon` mezőket szándékosan kihagyjuk:
// ezeket a Player.vue / PlayerPenalty.vue `checkWatch()` az óra alapján
// folyamatosan maga állítja.
function penaltySignature(penalties) {
    if (!Array.isArray(penalties)) return null;
    return penalties.map(p => p.id + ':' + p.time.start + ':' + p.time.stop + ':' + (p.scapegoat ? 1 : 0)).join('|');
}

const state = {
    gamedata: {},
    timelineData: [],
    dmrTimeline: [],
    dmrPlayers: [],
    substituteTimeline: [],
    currentPeriod: '1',
    sortedList: [],
    periodItems: [],
    actualGoalkeepers: {
        teamA: null,
        teamB: null
    },
    selection: {
        playerId: null,
        actionType: null
    },
    teamA: {
        team_id: 1,
        component: {
            position: 'appBar',
            direction: 'left'
        },
        isAttacking: false,
        isActive: false,
        arrowDirection: 'left',
        selectedGoalkeeper: false,
        totalUsedTimeOuts: 0,
        score: 0,
        timeOuts: [
            { id: 1, used: false, lost: false },
            { id: 2, used: false, lost: false },
            { id: 3, used: false, lost: false }
        ]
    },
    teamB: {
        team_id: 2,
        component: {
            position: 'appBar',
            direction: 'right'
        },
        arrowDirection: 'right',
        isAttacking: true,
        isActive: true,
        selectedGoalkeeper: false,
        totalUsedTimeOuts: 0,
        score: 0,
        timeOuts: [
            { id: 1, used: false, lost: false },
            { id: 2, used: false, lost: false },
            { id: 3, used: false, lost: false }
        ]
    },
    yellows: {
        players: {
            teamA: { given: 0, max: 3 },
            teamB: { given: 0, max: 3 },
        },
        officials: {
            teamA: { given: 0, max: 1 },
            teamB: { given: 0, max: 1 },
        },
    },
    officialPenaltiesGiven: {
        A: false,
        B: false,
    },
    gameFinished: false,
    draftCount: 0,
    rejectedCount: 0,
    syncablecount: 0,
    teamScores: null,
    teamSetScores: null,
    isWritingTimeout: false,
    editingRecord: null
};

const getters = {
    getAllPlayerStats: (state) => {
        const stats = {};
        const isStrand = state.gamedata.rule === 'STRAND';

        const ensurePlayerStat = (playerId) => {
            if (!stats[playerId]) {
                stats[playerId] = {
                    goals: 0,
                    yellow: false,
                    red: false,
                    blue: false,
                    strandYellows: 0,
                    showStrandYellow1: false,
                    showStrandYellow2: false
                };
            }
            return stats[playerId];
        };

        if (state.timelineData && state.timelineData.length) {
            state.timelineData.forEach(event => {
                // JAVÍTÁS: l. TimelineList.vue/ActionBar.vue azonos javítása -
                // a DMR törlése boolean `true`-t ír a `deleted` mezőbe, nem
                // numerikus `1`-et, ez a feltétel korábban csak az utóbbit
                // ismerte fel.
                if (event.deleted === 1 || event.deleted === true) return;

                const actionId = event.params && event.params.selectedAction ? event.params.selectedAction.actionId : null;
                const playerId = event.params && event.params.selectedPlayer ? event.params.selectedPlayer : null;

                if (playerId) {
                    const pStat = ensurePlayerStat(playerId);

                    if (actionId) {
                        if (CONSTANTS.ACTIONS.SINGLE_SCORE_ACTIONS.includes(actionId)) {
                            pStat.goals += 1;
                        } else if (CONSTANTS.ACTIONS.DOUBLE_SCORE_ACTIONS.includes(actionId)) {
                            pStat.goals += 2;
                        }
                    }

                    if (event.action === 'sargalap') pStat.yellow = true;
                    if (event.action === 'piroslap') pStat.red = true;
                    if (event.action === 'keklap') pStat.blue = true;

                    if (isStrand && event.action === 'kiallitas') {
                        pStat.strandYellows += 1;
                        if (pStat.strandYellows >= 1) pStat.showStrandYellow1 = true;
                        if (pStat.strandYellows >= 2) pStat.showStrandYellow2 = true;
                    }
                }
            });
        }
        return stats;
    },

    canTeamAskTimeOut: (state, getters, rootState) => (teamId) => {
        const team = teamId === state.teamA.team_id ? state.teamA : state.teamB;
        if (!team) return false;

        const isAttacking = team.isAttacking;
        // Ez a globális limit (max 3)
        const availableSlots = team.timeOuts.filter(t => !t.used && !t.lost).length;

        const totalTime = window.stopwatch.getTime();
        const isClockRunning = rootState.ui.tickInProgress;

        if (totalTime === 0 && !isClockRunning) return false;
        if (!isAttacking) return false;
        if (availableSlots <= 0) return false;

        // --- ÚJ RÉSZ: Félidőnkénti limit ellenőrzése (Max 2) ---
        const teamKey = teamId === state.teamA.team_id ? 'teamA' : 'teamB';
        const currentPeriod = state.currentPeriod;

        // Megszámoljuk, mennyi időkérése volt a csapatnak EBBEN a félidőben
        const usedInCurrentPeriod = state.timelineData.filter(e =>
            !e.deleted &&
            e.action === 'timeOut' &&
            e.params &&
            e.params.selectedTeamId === teamKey &&
            e.params.type === 'used' &&
            e.period === currentPeriod
        ).length;

        // Ha már ellőtték a 2-t a félidőben, nem kérhetnek többet (akkor sem, ha amúgy még van slot)
        if (usedInCurrentPeriod >= 2) {
            return false;
        }
        // -------------------------------------------------------

        return true;
    },

    getPlayerById: (state) => (id) => {
        const lists = [
            state.gamedata.team_a_players || [],
            state.gamedata.team_b_players || [],
            state.gamedata.team_a_officials || [],
            state.gamedata.team_b_officials || []
        ];
        return lists.flat().find(p => p.id === id);
    },

    getPlayerPermissions: (state, getters) => (playerId) => {
        const player = getters.getPlayerById(playerId);
        if (!player) return null;

        const now = window.stopwatch.getTotalTime();
        const isTeamA = player.team === 'teamA' || player.team === 'A';
        const teamIsAttacking = isTeamA ? state.teamA.isAttacking : state.teamB.isAttacking;
        const isDefender = !teamIsAttacking;
        const isOfficial = player.official === true;

        let hasYellow = player.cards.yellow.value;
        let hasRed = player.cards.red.value;
        let hasBlue = player.cards.blue.value;
        let penalties = player.penalties.length;

        // Szerkesztés logika: a szerkesztett lapot "nem létezőnek" tekintjük a validációhoz
        if (state.editingRecord && state.editingRecord.params && state.editingRecord.params.selectedPlayer === player.id) {
            const action = state.editingRecord.action;
            if (action === 'sargalap') hasYellow = false;
            if (action === 'piroslap') hasRed = false;
            if (action === 'keklap') hasBlue = false;
        }

        const redGivenTime = player.cards.red.given;
        const blueGivenTime = player.cards.blue.given;

        // Végleges kiállítás ellenőrzése
        const isDisqualified = (hasRed && redGivenTime <= now) || (hasBlue && blueGivenTime <= now);

        // --- AKTÍV BÜNTETÉS ELLENŐRZÉSE ---
        // Ha van olyan büntetése, ami épp pörög (isActive)
        const activePenalties = player.penalties.filter(p => p.isActive).length > 0;

        let ownPenaltiesCount = player.penalties.filter(p => !p.scapegoat).length;

        // Szerkesztés logika: 2 és 4 percesek korrekciója
        if (state.editingRecord &&
            state.editingRecord.params &&
            state.editingRecord.params.selectedPlayer === player.id) {

            if (['2perc', '4perc'].includes(state.editingRecord.action)) {
                let decrement = (state.editingRecord.action === '4perc') ? 2 : 1;
                ownPenaltiesCount = Math.max(0, ownPenaltiesCount - decrement);
            }
        }

        const teamKey = isTeamA ? 'teamA' : 'teamB';
        const typeKey = isOfficial ? 'officials' : 'players';
        const teamYellows = state.yellows[typeKey][teamKey];

        let effectiveGiven = teamYellows.given;

        if (state.editingRecord) {
            if (state.editingRecord.action === 'sargalap') {
                const editedTeamId = state.editingRecord.params ? state.editingRecord.params.selectedTeamId : null;
                // JAVÍTÁS: hivatalos személy (staff) lapjánál a params.selectedTeamId
                // értéke 'staffA'/'staffB', NEM 'teamA'/'teamB' - az eredeti
                // közvetlen "=== teamKey" összehasonlítás emiatt sosem talált
                // egyezést hivatalos személyeknél, így a szerkesztett lap sosem
                // lett kivonva a limitszámításból, és a már betelt (max=1) csapat-
                // hivatalos sárga lap limit véglegesen letiltva tartotta a saját
                // szerkesztését is. A csapat-azonosítót ezért 'staffX'->'teamX'-re
                // normalizáljuk összehasonlítás előtt.
                const normalizedEditedTeamId = (editedTeamId === 'staffA') ? 'teamA' : (editedTeamId === 'staffB') ? 'teamB' : editedTeamId;
                if (normalizedEditedTeamId === teamKey) {
                    effectiveGiven = Math.max(0, effectiveGiven - 1);
                }
            }
        }

        const maxYellowsReached = effectiveGiven >= teamYellows.max;

        let permissions = {
            // Sárgát csak akkor kaphat, ha:
            // 1. Nincs még neki
            // 2. Nincs piros/kék
            // 3. Nem telt be a csapat limit
            // 4. Nincs végleg kiállítva
            // 5. ÉS NINCS ÉPPEN IDŐLEGES KIÁLLÍTÁSON (activePenalties) [ÚJ FELTÉTEL]
            canReceiveYellow: !hasYellow && !hasRed && !hasBlue && !maxYellowsReached && !isDisqualified && !activePenalties && penalties < 1,

            // Pirosat csak akkor kaphat, ha:
            // 1. Nincs piros/kék
            // 2. ÉS NINCS ÉPPEN IDŐLEGES KIÁLLÍTÁSON (activePenalties) [ÚJ FELTÉTEL]
            canReceiveRed: !hasRed && !hasBlue,

            canReceiveBlue: hasRed && !hasBlue && (redGivenTime <= now),

            isDisqualified: isDisqualified,

            // Cselekvés (lövés, stb): tiltva ha védő, hivatalos, büntetésen van, vagy kizárva
            canPerformAction: !isDefender && !isOfficial && !activePenalties && !isDisqualified,

            canReceive2Min: true,
            canReceive4Min: true
        };

        if (hasYellow && !hasRed && !hasBlue) {
            permissions.canReceiveYellow = false;
        } else if (isDisqualified) {
            // Ha végleg ki van állítva, mindent letiltunk
            permissions.canReceiveYellow = false;
            permissions.canReceiveRed = false;
            permissions.canReceiveBlue = !hasBlue;
            permissions.canPerformAction = false;
        }

        const officialPenaltyAlreadyGiven = isOfficial && state.officialPenaltiesGiven[isTeamA ? 'A' : 'B'];

        // --- 2 PERC JOGOSULTSÁG ---
        // Tiltjuk, ha:
        // 1. Betelt a limit (3 db)
        // 2. Végleg ki van állítva
        // 3. Hivatalos személy már kapott
        // 4. ÉPPEN BÜNTETÉSEN VAN (activePenalties) [ÚJ FELTÉTEL]
        if ((ownPenaltiesCount + 1 > player.maxPenalties) || isDisqualified || officialPenaltyAlreadyGiven) {
            permissions.canReceive2Min = false;
        }

        // --- 4 PERC JOGOSULTSÁG ---
        // Tiltjuk, ha:
        // 1. Túl sok lenne a büntetés (2+2 > 3)
        // 2. Végleg ki van állítva
        // 3. Hivatalos személy (ők nem kaphatnak 2+2-t)
        // 4. ÉPPEN BÜNTETÉSEN VAN (activePenalties) [ÚJ FELTÉTEL]
        if ((ownPenaltiesCount + 2 > player.maxPenalties) || isDisqualified || isOfficial) {
            permissions.canReceive4Min = false;
        }

        return permissions;
    },
    getTeamGoalkeepers: (state, getters) => (team) => {
        const players = team === 'A' ? state.gamedata.team_a_players : state.gamedata.team_b_players;
        if (!players || !Array.isArray(players)) return [];

        let gks = players.filter(p => p.goalkeeper).map(p => {
            const perms = getters.getPlayerPermissions(p.id);
            const isDisabled = perms ? perms.isDisqualified : false;
            return {
                name: p.jersey ? `${p.jersey} - ${p.name}` : p.name,
                value: p.id,
                jersey: p.jersey,
                disabled: isDisabled
            };
        });
        gks.push({id: null, name: "Üres Kapu", value: 0, disabled: false});
        return gks;
    },

    getGamedata: state => state.gamedata,
    isGameLoaded: state => Object.keys(state.gamedata).length > 0,
    getTimelineData: state => state.timelineData,
    getDmrTimeline: state => state.dmrTimeline,
    getDmrPlayers: state => state.dmrPlayers,
    getSubstituteTimeline: state => state.substituteTimeline,
    getSortedList: state => state.sortedList,
    getCurrentPeriod: state => state.currentPeriod,
    getPeriodItems: state => state.periodItems,
    getActualGoalKeepers: state => state.actualGoalkeepers,
    getTeamA: state => state.teamA,
    getTeamB: state => state.teamB,
    getYellows: state => state.yellows,
    getOfficialPenaltiesGiven: state => state.officialPenaltiesGiven,
    isGameFinished: state => state.gameFinished,
    getDraftCount: state => state.draftCount,
    getRejectedCount: state => state.rejectedCount,
    getSyncableCount: state => state.syncablecount,
    getTeamScores: state => state.teamScores,
    getTeamSetScores: state => state.teamSetScores,
    maxTeamSize() {
        return Math.max.apply(null, [16, state.gamedata.team_a_players.length, state.gamedata.team_b_players.length]);
    },
    getPeriodData: (state) => (periodId) => {
        if (!state.gamedata.periods) return null;
        return state.gamedata.periods.find(p => p.value === String(periodId));
    },

    // Megmondja, hogy passzív-e a periódus (szünet)
    isPassivePeriod: (state, getters) => (periodId) => {
        const pData = getters.getPeriodData(periodId);
        if (!pData || !pData.type) return false;
        return ['zero', 'extendedzero'].includes(pData.type);
    },

    // Csapat azonosító konvertáló (A -> team_a_players)
    getTeamKey: () => (teamShort) => {
        return (teamShort === 'A' || teamShort === 'teamA') ? 'team_a_players' : 'team_b_players';
    },

    // Visszaadja a periódus kezdőidejét másodpercben
    getPeriodStartTime: (state, getters) => (periodId) => {
        const pData = getters.getPeriodData(periodId);
        return pData ? parseInt(pData.startat || 0) : 0;
    },

    // JAVÍTÁS (79. tétel - 32021424205): melyik periódusba kerüljön egy, a
    // `periodId` alatt rögzített esemény. Normál periódusban önmaga. Passzív
    // periódusban ("Félidő", "Mérkőzés vége", "... vége") az a LEJÁTSZOTT
    // normál periódus, amelyik pontosan ennek a kezdetekor ért véget - pl. a
    // lefújás után elvégzett hetes a "Mérkőzés vége" alatt rögzítve a II.
    // félidőbe kerül (60:00), nem a "Mérkőzés vége" periódusba. (Az ERP a
    // végeredményt minden gólból, a félidők eredményét periódusonként
    // számolja - a "Mérkőzés vége" alá került gól így egyik félidőbe sem
    // számított, és a beküldött adat ellentmondásos lett.)
    // A `startat` csak a ténylegesen elért periódusokat számolja (l.
    // periodStartat.js), ezért egy le nem játszott hosszabbítás vége sosem esik
    // egybe a "Mérkőzés vége" kezdetével - oda nem kerülhet esemény.
    getRecordingPeriod: (state, getters) => (periodId) => {
        const current = getters.getPeriodData(periodId);
        if (!current || !getters.isPassivePeriod(periodId)) return String(periodId);
        const currentStart = parseInt(current.startat || 0);
        let candidate = current.previousValue ? getters.getPeriodData(current.previousValue) : null;
        let guard = 0;
        while (candidate && guard++ < 50) {
            const duration = parseInt(candidate.duration) || 0;
            const start = parseInt(candidate.startat || 0);
            if (!getters.isPassivePeriod(candidate.value) && duration > 0 && start + duration === currentStart) {
                return String(candidate.value);
            }
            candidate = candidate.previousValue ? getters.getPeriodData(candidate.previousValue) : null;
        }
        return String(periodId);
    }
};

const mutations = {
    UPDATE_TIMELINE_EVENT(state, eventData) {
        const incomingUUID = eventData.UUID;
        const incomingId = eventData.id;
        const incomingKey = incomingUUID || incomingId;

        const index = state.timelineData.findIndex(e => {
            if (incomingUUID && (e.UUID === incomingUUID || e.id === incomingUUID)) return true;
            if (incomingId && (e.UUID === incomingId || e.id === incomingId)) return true;
            return false;
        });

        if (index !== -1) {
            const existingEvent = state.timelineData[index];

            // VÉDELEM: Ha a helyi esemény még szinkronizálatlan (synced=false),
            // és a bejövő adat NEM egy újabb helyi módosítás...
            if (existingEvent.synced === false && eventData.synced !== false) {
                // A bejövő adat a szerverről jön (DMRPUSH echo, STOREDONE, stb.)
                // Csak akkor engedjük felülírni, ha a szerver adata FRISSEBB (vagy egy
                // AZONOS clientTime-ú visszhang a SAJÁT írásunkról - l. lentebb)
                // ÉS nem veszít el helyi módosítást (pl. approved státusz).
                // JAVÍTÁS: eredetileg szigorú `>` állt itt, ami azt jelentette, hogy a
                // syncDone() saját, ugyanazt a rekordot (AZONOS clientTime-mal) visszaolvasó
                // "most már synced" visszaigazolása sosem tudott áthaladni ezen a védelmen -
                // a Vuex-beli másolat örökre "synced=false" maradt, ami minden későbbi
                // reconnect-merge-öt megzavart (a régi helyi adat mindig "szinkronizálatlanként"
                // nyert a frissebb szerver-adattal szemben). `>=` mellett a saját visszhang
                // most már átmegy, miközben egy VALÓBAN régebbi (kisebb clientTime-ú) bejövő
                // adat továbbra is blokkolva marad.
                const serverIsNewer = eventData.clientTime && existingEvent.clientTime && eventData.clientTime >= existingEvent.clientTime;
                const wouldLoseApproval = existingEvent.approved !== undefined && eventData.approved === undefined;
                const wouldLoseParams = existingEvent.params && eventData.params === undefined;

                if (!serverIsNewer || wouldLoseApproval || wouldLoseParams) {
                    console.log(`[SYNC-GUARD] Timeline event BLOCKED. ID: ${incomingKey} | serverIsNewer: ${serverIsNewer} | wouldLoseApproval: ${wouldLoseApproval} | wouldLoseParams: ${wouldLoseParams} | existing.clientTime: ${existingEvent.clientTime} | incoming.clientTime: ${eventData.clientTime} | existing.synced: ${existingEvent.synced} | incoming.synced: ${eventData.synced}`);
                    // Védjük a helyi módosítást, de szinkronizáljuk az ID-kat ha szükséges
                    if (eventData.id && !existingEvent.id) {
                        Vue.set(state.timelineData[index], 'id', eventData.id);
                    }
                    if (eventData.UUID && !existingEvent.UUID) {
                        Vue.set(state.timelineData[index], 'UUID', eventData.UUID);
                    }
                    return;
                }
                console.log(`[SYNC-GUARD] Timeline event ALLOWED through guard. ID: ${incomingKey} | incoming.synced: ${eventData.synced}`);
            }

            const updatedEvent = { ...existingEvent, ...eventData };
            if (existingEvent.params && eventData.params) {
                updatedEvent.params = { ...existingEvent.params, ...eventData.params };
            }

            // Vue.set használata a garantált reaktivitásért
            Vue.set(state.timelineData, index, updatedEvent);

        } else {
            state.timelineData.push(eventData);
        }

        // DMR Timeline frissítése is Vue.set-tel a konzisztencia érdekében
        if (state.dmrTimeline) {
            const dmrIndex = state.dmrTimeline.findIndex(e => {
                if (incomingUUID && (e.UUID === incomingUUID || e.id === incomingUUID)) return true;
                if (incomingId && (e.UUID === incomingId || e.id === incomingId)) return true;
                return false;
            });

            if (dmrIndex !== -1) {
                const existingDmrEvent = state.dmrTimeline[dmrIndex];

                if (existingDmrEvent.synced === false && eventData.synced !== false) {
                    // JAVÍTÁS: ugyanaz a `>` -> `>=` javítás, mint a fenti Fő Timeline
                    // blokkban - lásd az ottani kommentet a gyökérokról.
                    const serverIsNewer = eventData.clientTime && existingDmrEvent.clientTime && eventData.clientTime >= existingDmrEvent.clientTime;
                    const wouldLoseApproval = existingDmrEvent.approved !== undefined && eventData.approved === undefined;
                    const wouldLoseParams = existingDmrEvent.params && eventData.params === undefined;

                    if (!serverIsNewer || wouldLoseApproval || wouldLoseParams) {
                        console.log(`[SYNC-GUARD-DMR] DMR event BLOCKED. ID: ${incomingKey} | serverIsNewer: ${serverIsNewer} | wouldLoseApproval: ${wouldLoseApproval} | wouldLoseParams: ${wouldLoseParams} | existing.clientTime: ${existingDmrEvent.clientTime} | incoming.clientTime: ${eventData.clientTime} | existing.synced: ${existingDmrEvent.synced} | incoming.synced: ${eventData.synced} | existing.params.selectedPlayer: ${existingDmrEvent.params && existingDmrEvent.params.selectedPlayer} | incoming.params.selectedPlayer: ${eventData.params && eventData.params.selectedPlayer}`);
                        return;
                    }
                    console.log(`[SYNC-GUARD-DMR] DMR event ALLOWED through guard. ID: ${incomingKey}`);
                }

                const updatedDmrEvent = { ...existingDmrEvent, ...eventData };

                if (existingDmrEvent.params && eventData.params) {
                    updatedDmrEvent.params = { ...existingDmrEvent.params, ...eventData.params };
                }
                Vue.set(state.dmrTimeline, dmrIndex, updatedDmrEvent);
            }
        }
    },
    REMOVE_TIMELINE_EVENT(state, uuid) {
        const index = state.timelineData.findIndex(e => e.UUID === uuid || e.id === uuid);
        if (index !== -1) {
            state.timelineData.splice(index, 1);
        }
        if (state.dmrTimeline) {
            const dmrIdx = state.dmrTimeline.findIndex(e => e.UUID === uuid || e.id === uuid);
            if (dmrIdx !== -1) state.dmrTimeline.splice(dmrIdx, 1);
        }
    },
    ADD_PLAYER_TO_TEAM(state, {team, player}) {
        const targetList = team === 'A' ? state.gamedata.team_a_players : state.gamedata.team_b_players;
        if (targetList) targetList.push(player);
    },
    REMOVE_PLAYER_FROM_ENTRY(state, {team, playerId}) {
        const entryList = team === 'A' ? state.gamedata.team_a_players_entry : state.gamedata.team_b_players_entry;
        if (entryList) {
            const index = entryList.findIndex(p => p.id === playerId);
            if (index !== -1) entryList.splice(index, 1);
        }
    },
    SET_STRAND_SELECTION_FLAG(state, {team, value}) {
        if (team === 'A') state.teamA.selectedGoalkeeper = value;
        if (team === 'B') state.teamB.selectedGoalkeeper = value;
    },
    SET_GAMEDATA(state, data) {
        state.gamedata = data;
        window.gamedata = data;
    },
    SET_TIMELINE(state, timeline) {
        state.timelineData = Array.isArray(timeline) ? timeline : [];
    },
    // JAVÍTÁS (TELJESÍTMÉNY, 77. tétel): a reCalculateScores eredménye. Csak
    // azoknál az eseményeknél cseréli a `params.teamScore`-t, ahol az állás
    // ténylegesen változott, a lista pedig ugyanazokat az (már reaktív)
    // objektumokat kapja vissza új sorrendben - nem mély másolatot.
    APPLY_TIMELINE_SCORES(state, {timeline, scoreUpdates}) {
        scoreUpdates.forEach(({item, score}) => {
            Vue.set(item.params, 'teamScore', score);
        });
        state.timelineData = Array.isArray(timeline) ? timeline : [];
    },
    SET_SUBSTITUTE_TIMELINE(state, timeline) {
        state.substituteTimeline = timeline;
    },
    SET_DMR_DATA(state, {timeline, players}) {
        state.dmrTimeline = timeline;
        state.dmrPlayers = players;
    },
    SET_SORTED_LIST(state, list) {
        state.sortedList = list;
    },
    SET_GOALKEEPER(state, {team, playerId}) {
        state.actualGoalkeepers[team === 'A' ? 'teamA' : 'teamB'] = playerId;
        if (team === 'A') state.gamedata.team_a_actual_goalkeeper.id = playerId;
        if (team === 'B') state.gamedata.team_b_actual_goalkeeper.id = playerId;
    },
    SET_SELECTED_PLAYER(state, playerId) {
        state.selection.playerId = playerId;
    },
    SET_PERIOD(state, period) {
        state.currentPeriod = period;
        window.period = period;

        // A periódusok "startat" (kezdő idő) mezőjét eredetileg csak a
        // loadGameData() (belépés/reconnect) számolta ki, egyszer - onnantól a
        // session hátralévő részében SOHA nem frissült, ezért egy hosszú,
        // reconnect nélküli mérkőzésen a hosszabbítás-periódusok kezdő ideje
        // elavult maradt. Ezért számoljuk újra itt is, minden periódusváltáskor.
        // Mivel a `window.gamedata` és a `state.gamedata` UGYANARRA az objektumra
        // mutat (l. SET_GAMEDATA), a `state.gamedata.periods` elemeinek itteni
        // frissítése a Stopwatch.js `getTrueTotalTime()`-ját (ami window.gamedata-ból
        // olvas) is automatikusan naprakészen tartja, külön módosítás nélkül.
        if (state.gamedata && Array.isArray(state.gamedata.periods)) {
            // A számítás a közös `periodStartat.js`-ben él - l. az ottani
            // részletes magyarázatot arról, miért NEM szabad ezt a logikát
            // több helyen, egymástól függetlenül implementálni.
            applyPeriodStartAt(
                state.gamedata.periods,
                collectReachedPeriods(state.timelineData, state.dmrTimeline),
                period
            );
        }
    },
    SET_PERIOD_ITEMS(state, items) {
        state.periodItems = items;
    },
    UPDATE_SCORE(state, {team, score}) {
        if (team === 'A') state.teamA.score = score;
        if (team === 'B') state.teamB.score = score;
    },
    SET_ATTACKING_DIRECTION(state, direction) {
        state.teamA.isAttacking = (direction === 'left');
        state.teamB.isAttacking = (direction === 'right');
        state.teamA.isActive = (direction === 'left');
        state.teamB.isActive = (direction === 'right');
    },
    SET_TEAM_TIMEOUTS(state, {team, timeOuts, totalUsed}) {
        const targetTeam = team === 'A' ? state.teamA : state.teamB;
        if (timeOuts) targetTeam.timeOuts = timeOuts;
        if (totalUsed !== undefined) targetTeam.totalUsedTimeOuts = totalUsed;
    },
    SET_YELLOW_LIMITS(state) {
        const isStrand = state.gamedata.rule === 'STRAND';
        const maxPlayers = isStrand ? Infinity : 3;
        const maxOfficials = isStrand ? Infinity : 1;
        state.yellows.players.teamA.max = maxPlayers;
        state.yellows.players.teamB.max = maxPlayers;
        state.yellows.officials.teamA.max = maxOfficials;
        state.yellows.officials.teamB.max = maxOfficials;
    },
    UPDATE_YELLOW_COUNT(state, {type, team, change}) {
        if (state.yellows[type] && state.yellows[type][team]) {
            state.yellows[type][team].given += change;
        }
    },
    SET_OFFICIAL_PENALTY_GIVEN(state, {team, value}) {
        state.officialPenaltiesGiven[team] = value;
    },
    SET_GAME_FINISHED(state, value) {
        state.gameFinished = value;
    },
    SET_DRAFT_COUNT(state, count) {
        state.draftCount = count;
    },
    SET_REJECTED_COUNT(state, count) {
        state.rejectedCount = count;
    },
    SET_SYNCABLE_COUNT(state, count) {
        state.syncablecount = count;
    },
    SET_DYNAMIC_COMPONENTS(state, {teamScores, teamSetScores}) {
        if (teamScores) state.teamScores = teamScores;
        if (teamSetScores) state.teamSetScores = teamSetScores;
    },
    UPDATE_GAMEDATA_SCORES(state, {teamA, teamB}) {
        if (state.gamedata) {
            state.gamedata.team_a_score = teamA;
            state.gamedata.team_b_score = teamB;
            if (window.gamedata) {
                window.gamedata.team_a_score = teamA;
                window.gamedata.team_b_score = teamB;
            }
        }
    },
    SET_WRITING_TIMEOUT(state, val) {
        state.isWritingTimeout = val;
    },
    SET_EDITING_RECORD(state, record) {
        state.editingRecord = record;
    }
};

const actions = {
    /**
     * Robusztus újracsatlakozás kezelő.
     * Szekvenciális folyamatot valósít meg:
     * 1. Először feltölti a helyi, offline módosításokat.
     * 2. Utána letölti a szerverről a friss, összefésült állapotot.
     */
    async handleReconnect({ dispatch, state }) {
        console.log(`[Game] Reconnect detected. Starting robust sync process...`);

        // 1. Lépés: Töltsük fel a helyi, nem szinkronizált változásokat a szerverre.
        // Ez biztosítja, hogy a szerver tudomást szerezzen az offline munkánkról,
        // mielőtt mi letöltenénk az ő állapotát.
        try {
            console.log("[SYNC] Step 1: Pushing offline changes to server...");
            await dispatch('syncOfflineData');
            console.log("[SYNC] Step 1 complete.");
        } catch (e) {
            console.error("[SYNC] Critical error during offline data push:", e);
            // Hiba esetén is megpróbáljuk betölteni a szerver adatot, hogy legalább valami legyen.
        }

        // 2. Lépés: Töltsük le a szerverről a legfrissebb, összefésült állapotot.
        // A 'loadGameData' már tartalmazza a robusztus "Smart Merge" logikát,
        // ami kezeli az esetlegesen még fennmaradó ütközéseket.
        console.log("[SYNC] Step 2: Fetching latest state from server...");
        await dispatch('loadGameData');
        console.log("[SYNC] Step 2 complete. Sync process finished.");


        // 3. Lépés: UI frissítése a felületnek megfelelően.
        // Ez a rész változatlan marad, a friss, szinkronizált adatokon fut.
        const urlParams = new URLSearchParams(window.location.search);
        if (window.usertype === 'report' || urlParams.get('felulet') === 'JEGYZOKONYV') {
            dispatch('reCalculateScores', { totalGameTime: 0, sync: false });
            dispatch('recalculateDrafts');
        }
        else if (window.usertype === 'dmr' || urlParams.get('felulet') === 'dmr') {
            dispatch('dmr/initDmr', null, { root: true });
        }
    },

    /**
     * Robusztus "Smart Merge" (Okos összefésülés) a szerverről érkező adatokkal.
     * Prioritási sorrend:
     * 1. A TÖRLÉS végleges. Ha egy esemény bárhol törölve van, törölve marad.
     * 2. A HELYI, NEM SZINKRONIZÁLT módosítás ('synced: false') felülírja a szerverről jövőt.
     * 3. Minden más esetben a szerver adata a mérvadó.
     */
    async loadGameData({commit, dispatch, state, rootGetters}) {
        try {
            // --- 1. VÉDELEM: CSAK SUB FELÜLETEN ÉS FUTÓ ÓRÁNÁL TILTUNK ---
            const isClockPaused = rootGetters['clock/isPaused'];
            const isSubInterface = (window.usertype === 'sub');

            if (isSubInterface && typeof isClockPaused !== 'undefined' && !isClockPaused) {
                console.log("Vuex: loadGameData SKIPPED (SUB + Running Clock) - Flicker védelem aktív.");
                return false;
            }

            console.log("Vuex: loadGameData started");
            const result = await window.dbmanager.gamedata();

            if (!result) return false;

            const params = result.params || {};

            // --- BIZTONSÁGOS FLICKER VÉDELEM ---
            const tempScoreA = (state.gamedata && state.gamedata.team_a_score !== undefined) ? state.gamedata.team_a_score : params.team_a_score;
            const tempScoreB = (state.gamedata && state.gamedata.team_b_score !== undefined) ? state.gamedata.team_b_score : params.team_b_score;

            params.team_a_score = tempScoreA;
            params.team_b_score = tempScoreB;
            // -----------------------------------

            params.maxTimeOuts = params.rule === 'STRAND' ? 2 : 3;

            commit('SET_GAMEDATA', params);

            if (params.team_a_actual_goalkeeper && params.team_a_actual_goalkeeper.id) {
                commit('SET_GOALKEEPER', {team: 'A', playerId: params.team_a_actual_goalkeeper.id});
                commit('SET_STRAND_SELECTION_FLAG', {team: 'A', value: true});
            }
            if (params.team_b_actual_goalkeeper && params.team_b_actual_goalkeeper.id) {
                commit('SET_GOALKEEPER', {team: 'B', playerId: params.team_b_actual_goalkeeper.id});
                commit('SET_STRAND_SELECTION_FLAG', {team: 'B', value: true});
            }

            // Mezőnkénti egyesítés: ha a helyi, még nem szinkronizált rekord `partialUpdate`-es
            // (pl. DMR jóváhagyás/elutasítás), akkor NEM a teljes helyi rekord nyer, hanem a
            // szerver (frissebb, pl. CR által időközben javított) tartalmi mezői maradnak meg,
            // és csak a DMR-saját jóváhagyási mezők kerülnek rá.
            const PARTIAL_UPDATE_FIELDS = ['approved', 'rejectReason', 'message', 'checked', 'isDraft', 'draft'];
            const applyPartialUpdate = (serverItem, localItem) => {
                const merged = {...serverItem};
                PARTIAL_UPDATE_FIELDS.forEach(f => {
                    if (typeof localItem[f] !== 'undefined') merged[f] = localItem[f];
                });
                merged.params = {...(serverItem.params || {})};
                if (localItem.params) {
                    PARTIAL_UPDATE_FIELDS.forEach(f => {
                        if (typeof localItem.params[f] !== 'undefined') merged.params[f] = localItem.params[f];
                    });
                }
                merged.synced = localItem.synced;
                merged.partialUpdate = localItem.partialUpdate;
                merged.clientTime = localItem.clientTime;
                return merged;
            };

            // --- 2. ROBUST SMART MERGE (Fő Timeline) ---
            console.log("[SYNC] Starting robust merge for Main Timeline...");
            let serverTimeline = Array.isArray(result.timelineData) ? result.timelineData : [];
            let localTimeline = state.timelineData || [];
            const mergedMap = new Map();

            // 1. lépés: Betöltjük a szerverről érkező összes eseményt a map-be.
            serverTimeline.forEach(item => {
                const key = item.UUID || item.id;
                if (key) mergedMap.set(key, item);
            });

            // 2. lépés: A lokális, még nem szinkronizált változások MINDIG felülírják a szerver adatait.
            // Ezek a felhasználó offline módosításai, amelyeket a sync() el fog küldeni.
            const unsyncedLocalEvents = localTimeline.filter(e => e.synced === false);
            console.log(`[SYNC] Local timelineData count: ${localTimeline.length} | unsynced: ${unsyncedLocalEvents.length} | IDs: ${unsyncedLocalEvents.map(e => e.UUID || e.id).join(', ')}`);
            unsyncedLocalEvents.forEach(localItem => {
                const key = localItem.UUID || localItem.id;
                if (key) {
                    const serverItem = mergedMap.get(key);
                    // JAVÍTÁS (2026-08-09, VISSZAVONVA egy korábbi, hibás
                    // "védőháló"): itt korábban egy `isGenuinePartial` ellenőrzés
                    // figyelmen kívül hagyta a `partialUpdate` jelzőt, ha a helyi
                    // `params` a jóváhagyás-mezőkön túl tartalmi mezőket is
                    // tartalmazott - abból a (téves) feltevésből, hogy ez csak egy
                    // tévesen megjelölt teljes szerkesztés lehet. A DMR SAJÁT,
                    // NORMÁLIS jóváhagyás/elutasítás rekordja is MINDIG pontosan
                    // így néz ki (a helyi másolat szándékosan teljes) - ez a
                    // védőháló emiatt a DMR minden döntését feltétel nélküli
                    // TELJES íróvá alakította a reconnect-merge-nél is, elveszítve
                    // a mező-szintű összefésülés védelmét egy frissebb szerver-
                    // oldali (pl. CR-től származó) tartalommal szemben. A
                    // tényleges gyökérokot (ActionBar.vue "manualsave") már
                    // közvetlenül kijavítottuk - l. `dbmanager.js` sync()
                    // azonos visszavonása.
                    if (localItem.partialUpdate === true && serverItem) {
                        console.log(`[SYNC] Local unsynced PARTIAL event merged field-level over server data. ID: ${key} | server.params.selectedPlayer: ${serverItem.params && serverItem.params.selectedPlayer}`);
                        mergedMap.set(key, applyPartialUpdate(serverItem, localItem));
                    } else {
                        console.log(`[SYNC] Local unsynced event takes priority (WHOLE RECORD, no partialUpdate flag). ID: ${key} | Action: ${localItem.action} | had server counterpart: ${!!serverItem}`);
                        mergedMap.set(key, localItem);
                    }
                }
            });

            // 3. lépés: Abszolút prioritást adunk a törléseknek.
            // Ha egy esemény BÁRHOL (lokálisan vagy szerveren) törölve van, akkor az törölve marad.
            const allItemsForDeletionCheck = [...serverTimeline, ...localTimeline];
            allItemsForDeletionCheck.forEach(item => {
                if (item.deleted === 1 || item.deleted === true) {
                    const key = item.UUID || item.id;
                    if (key && mergedMap.has(key)) {
                        const finalItem = mergedMap.get(key);
                        if(finalItem.deleted !== 1 && finalItem.deleted !== true) {
                            console.log(`[SYNC] Deletion enforced for event ID: ${key}`);
                            finalItem.deleted = 1;
                            mergedMap.set(key, finalItem);
                        }
                    }
                }
            });

            const mergedTimeline = Array.from(mergedMap.values());
            commit('SET_TIMELINE', mergedTimeline);
            console.log(`[SYNC] Main Timeline merge complete. Final count: ${mergedTimeline.length}`);


            // --- 3. ROBUST SMART MERGE (DMR Timeline) ---
            console.log("[SYNC] Starting robust merge for DMR Timeline...");
            let serverDmrTimeline = Array.isArray(result.dmrTimeline) ? result.dmrTimeline : [];
            let localDmrTimeline = state.dmrTimeline || [];
            const dmrMergedMap = new Map();

            // 1. lépés: Szerver adatok betöltése.
            serverDmrTimeline.forEach(item => {
                const key = item.UUID || item.id;
                if (key) dmrMergedMap.set(key, item);
            });

            // 2. lépés: A lokális, nem szinkronizált DMR események MINDIG felülírják a szerver adatait.
            const unsyncedLocalDmr = localDmrTimeline.filter(e => e.synced === false);
            console.log(`[SYNC-DMR] Local dmrTimeline count: ${localDmrTimeline.length} | unsynced: ${unsyncedLocalDmr.length} | IDs: ${unsyncedLocalDmr.map(e => e.UUID || e.id).join(', ')}`);
            unsyncedLocalDmr.forEach(localItem => {
                const key = localItem.UUID || localItem.id;
                if (key) {
                    const serverItem = dmrMergedMap.get(key);
                    // JAVÍTÁS (2026-08-09, VISSZAVONVA): l. a Fő Timeline ágban lévő
                    // azonos visszavonás magyarázatát.
                    if (localItem.partialUpdate === true && serverItem) {
                        console.log(`[SYNC-DMR] Local unsynced PARTIAL DMR event merged field-level over server data. ID: ${key} | server.params.selectedPlayer: ${serverItem.params && serverItem.params.selectedPlayer} | local.params.approved: ${localItem.params && localItem.params.approved} | local.params.rejectReason: ${localItem.params && localItem.params.rejectReason}`);
                        dmrMergedMap.set(key, applyPartialUpdate(serverItem, localItem));
                    } else {
                        console.log(`[SYNC-DMR] Local unsynced DMR event takes priority (WHOLE RECORD, no partialUpdate flag). ID: ${key} | local.params.selectedPlayer: ${localItem.params && localItem.params.selectedPlayer} | had server counterpart: ${!!serverItem}`);
                        dmrMergedMap.set(key, localItem);
                    }
                }
            });

            // 3. lépés: Törlések érvényesítése a DMR timeline-on is.
            const allDmrItemsForDeletionCheck = [...serverDmrTimeline, ...localDmrTimeline];
            allDmrItemsForDeletionCheck.forEach(item => {
                if (item.deleted === 1 || item.deleted === true) {
                    const key = item.UUID || item.id;
                    if (key && dmrMergedMap.has(key)) {
                        const finalItem = dmrMergedMap.get(key);
                         if(finalItem.deleted !== 1 && finalItem.deleted !== true) {
                            console.log(`[SYNC-DMR] Deletion enforced for DMR event ID: ${key}`);
                            finalItem.deleted = 1;
                            dmrMergedMap.set(key, finalItem);
                        }
                    }
                }
            });

            const mergedDmrTimeline = Array.from(dmrMergedMap.values());
            console.log(`[SYNC] DMR Timeline merge complete. Final count: ${mergedDmrTimeline.length}`);

            // --- 4. ROBUST SMART MERGE (Csere/SUB Timeline) ---
            // JAVÍTÁS (2026-08-31): korábban a substituteTimeline egyszerűen felül lett
            // írva a szerver válaszával (`result.substituteTimeline`), a Fő Timeline-nál
            // és a DMR Timeline-nál fentebb már meglévő "a lokális, még nem
            // szinkronizált adat mindig nyer" védelem NÉLKÜL. Ha egy SUB-oldali esemény
            // (pl. egy Csere) még nem ért fel a szerverre - pl. egy oldal-újratöltés
            // pont egy ingatag kapcsolat közben, közvetlenül a szinkronizálás előtt
            // történt -, ez a felülírás CSENDBEN elvesztette a legutóbbi eseményt a
            // memóriából. Ez alapozta meg a SUB felület saját, önálló "ki van a
            // pályán" számítását (pl. félidő-váltáskori kezdőcsapat-választás) egy
            // régebbi, hiányos állapotra - egy valós meccsen (32021439419, Szuperkupa)
            // ez okozott hibás II. félidős kezdőcsapatot (egy játékos helyett egy
            // másikat mutatott pályán lévőnek).
            console.log("[SYNC] Starting robust merge for Substitute Timeline...");
            let serverSubstituteTimeline = Array.isArray(result.substituteTimeline) ? result.substituteTimeline : [];
            let localSubstituteTimeline = state.substituteTimeline || [];
            const substituteMergedMap = new Map();

            serverSubstituteTimeline.forEach(item => {
                const key = item.UUID || item.id;
                if (key) substituteMergedMap.set(key, item);
            });

            const unsyncedLocalSubstitute = localSubstituteTimeline.filter(e => e.synced === false);
            console.log(`[SYNC-SUB] Local substituteTimeline count: ${localSubstituteTimeline.length} | unsynced: ${unsyncedLocalSubstitute.length} | IDs: ${unsyncedLocalSubstitute.map(e => e.UUID || e.id).join(', ')}`);
            unsyncedLocalSubstitute.forEach(localItem => {
                const key = localItem.UUID || localItem.id;
                if (key) substituteMergedMap.set(key, localItem);
            });

            const allSubstituteItemsForDeletionCheck = [...serverSubstituteTimeline, ...localSubstituteTimeline];
            allSubstituteItemsForDeletionCheck.forEach(item => {
                if (item.deleted === 1 || item.deleted === true) {
                    const key = item.UUID || item.id;
                    if (key && substituteMergedMap.has(key)) {
                        const finalItem = substituteMergedMap.get(key);
                        if (finalItem.deleted !== 1 && finalItem.deleted !== true) {
                            console.log(`[SYNC-SUB] Deletion enforced for Substitute event ID: ${key}`);
                            finalItem.deleted = 1;
                            substituteMergedMap.set(key, finalItem);
                        }
                    }
                }
            });

            const mergedSubstituteTimeline = Array.from(substituteMergedMap.values());
            console.log(`[SYNC] Substitute Timeline merge complete. Final count: ${mergedSubstituteTimeline.length}`);

            commit('SET_SUBSTITUTE_TIMELINE', mergedSubstituteTimeline);
            commit('SET_DMR_DATA', { timeline: mergedDmrTimeline, players: result.dmrPlayers || [] });
            // --------------------------------------------------

            let serverPeriod = parseInt(result.period);
            let localPeriod = parseInt(state.currentPeriod);

            if (window.usertype === 'dmr' && !isNaN(serverPeriod) && !isNaN(localPeriod) && localPeriod > serverPeriod) {
                console.log(`Vuex: loadGameData - Period override ignored (DMR protection). Local: ${localPeriod}, Server: ${serverPeriod}`);
                // Nem hívjuk meg a commit('SET_PERIOD')-ot!
            } else {
                commit('SET_PERIOD', result.period);
            }
            // ----------------------------------------

            commit('SET_YELLOW_LIMITS');

            let lastGkA = params.team_a_actual_goalkeeper ? params.team_a_actual_goalkeeper.id : null;
            let lastGkB = params.team_b_actual_goalkeeper ? params.team_b_actual_goalkeeper.id : null;
            let foundChangeA = false;
            let foundChangeB = false;

            const allRecords = await window.dbmanager.readAll();
            if (allRecords && allRecords.length > 0) {
                const sortedRecords = [...allRecords].sort((a, b) => (a.date || a.clientTime || 0) - (b.date || b.clientTime || 0));
                sortedRecords.forEach(record => {
                    if (record.action === 'gamedatachange' && record.params) {
                        if (record.params.name === 'team_a_actual_goalkeeper') {
                            const val = record.params.value;
                            lastGkA = (val && val.id) ? val.id : null;
                            foundChangeA = true;
                        }
                        if (record.params.name === 'team_b_actual_goalkeeper') {
                            const val = record.params.value;
                            lastGkB = (val && val.id) ? val.id : null;
                            foundChangeB = true;
                        }
                    }
                });
            }

            if (foundChangeA || lastGkA) {
                commit('SET_GOALKEEPER', {team: 'A', playerId: lastGkA});
                commit('SET_STRAND_SELECTION_FLAG', {team: 'A', value: true});
            }
            if (foundChangeB || lastGkB) {
                commit('SET_GOALKEEPER', {team: 'B', playerId: lastGkB});
                commit('SET_STRAND_SELECTION_FLAG', {team: 'B', value: true});
            }

            // Irány számítás és egyéb (Változatlan)
            let currentDirection = 'right';
            if (state.currentPeriod === '3' || state.currentPeriod === '4') {
                currentDirection = 'left';
            }

            const sortedEvents = [...mergedTimeline].sort((a, b) => (a.date || a.clientTime || 0) - (b.date || b.clientTime || 0));

            for (let i = sortedEvents.length - 1; i >= 0; i--) {
                const event = sortedEvents[i];
                if (event.deleted) continue;

                let dir = null;
                if (event.params && event.params.attackDirection) {
                    dir = event.params.attackDirection;
                } else if (event.attackDirection) {
                    dir = event.attackDirection;
                }

                const action = event.action;
                const scoreActions = ['gol', 'gol7m', 'gol2p', 'gol6m', 'gol_ures_kapu'];
                const turnoverActions = ['technikai', 'eladott', 'belemenés', 'passziv', 'lepes', 'ketszerindulas'];
                const otherActions = ['changeDirection', 'timeOut'];

                const isScore = scoreActions.includes(action) || (CONSTANTS.ACTIONS.SINGLE_SCORE_ACTIONS && CONSTANTS.ACTIONS.SINGLE_SCORE_ACTIONS.includes(action)) || (CONSTANTS.ACTIONS.DOUBLE_SCORE_ACTIONS && CONSTANTS.ACTIONS.DOUBLE_SCORE_ACTIONS.includes(action));
                const isTurnover = turnoverActions.includes(action);
                const isOther = otherActions.includes(action);

                if (dir && (isScore || isTurnover || isOther)) {
                    currentDirection = dir;
                    if (isScore || isTurnover) {
                        currentDirection = (currentDirection === 'left' ? 'right' : 'left');
                    }
                    break;
                }
            }

            commit('SET_ATTACKING_DIRECTION', currentDirection);
            eventBus.$emit(CONSTANTS.EVENTS.CHANGE.DIRECTION, currentDirection);

            window.gamedata = {...params};

            dispatch('recalculateTimeOuts');
            dispatch('reCalculateScores', {totalGameTime: 0, sync: false});
            dispatch('recalculateDrafts');
            dispatch('recalculateRejecteds');
            dispatch('updateSortedTimeline');

            return result;
        } catch (error) {
            console.error("Vuex: Hiba a betöltéskor", error);
            return false;
        }
    },

    /**
     * Csak a helyi, nem szinkronizált eseményeket tölti fel a szerverre.
     */
    async syncOfflineData({state}) {
        console.log("[SYNC] Checking for local changes to push...");

        // JAVÍTÁS: a `partialUpdate:true` rekordokat (pl. DMR jóváhagyás/elutasítás)
        // itt KIHAGYJUK - ezeket a saját mentési útvonaluk (pl. DmrScreen.vue
        // saveAndSend) már közvetlenül, szinkronban kiírta az IndexedDB-be a döntés
        // pillanatában, tehát sosem az itteni write-back-re támaszkodnak elsőként.
        // Ha egy ilyen rekord `state.timelineData`-beli Vuex-másolata egy KORÁBBI
        // reconnect mezőnkénti összefésülése (`applyPartialUpdate`) miatt átmenetileg
        // `synced:false`-nak látszik (mert az akkori merge még a szerver visszaigazolása
        // ELŐTT futott le), ennek visszaírása ide - egy már réges-régen szinkronizált,
        // helyes IndexedDB-rekordot egy ELAVULT, összefésült hibrid példánnyal
        // felülcsapva - végtelenített hurkot okozott: minden reconnectnél újra
        // "szinkronizálatlanná" tette a rekordot, ami a dbmanager.sync() általános
        // mechanizmusán keresztül újra és újra elküldte a DMR RÉGI, esetleg már
        // idejétmúlt jóváhagyási döntését a szervernek - akár egy közben történt,
        // frissebb CR-javítás után is.
        const unsyncedEvents = state.timelineData.filter(e => e.synced === false && e.partialUpdate !== true);

        if (unsyncedEvents.length > 0) {
            console.log(`[SYNC] Found ${unsyncedEvents.length} unsynced events. Pushing to server...`);

            for (const event of unsyncedEvents) {
                try {
                    // FONTOS: Klónozzuk az eseményt, mielőtt átadjuk a dbmanager-nek,
                    // hogy megszabaduljunk a Vue reaktív Getter/Setter-eitől.
                    const cleanEvent = JSON.parse(JSON.stringify(event));

                    // JAVÍTÁS (74. tétel): ha a helyi adatbázisban a rekord MÁR
                    // szinkronizálatlan, nincs mit "visszatolni": a sync() amúgy
                    // is elküldi, mégpedig a helyi adatbázis - mint mérvadó helyi
                    // állapot - szerinti tartalommal. A visszaírás itt csak kárt
                    // okozott: az `update(..., false)` (a központi bélyegzés óta)
                    // minden ilyen eseménynek ÚJ `clientTime`-ot adott, így minden
                    // (újra)csatlakozás a teljes szinkronizálatlan listát új
                    // verzióvá tette; ha pedig a Vuex-példány egy korábbi
                    // összefésülés miatt elavult volt, felül is írta vele a
                    // helyi adatbázis frissebb tartalmát. A többi esetben (a
                    // rekord hiányzik a helyi adatbázisból, vagy ott már
                    // szinkronizáltnak látszik) változatlanul visszaírjuk -
                    // ez a biztonsági háló arra az esetre, ha egy korábbi helyi
                    // írás nem jutott el az adatbázisig.
                    const recordKey = cleanEvent.UUID || cleanEvent.id;
                    let stored = null;
                    try {
                        stored = recordKey ? await window.dbmanager.read(recordKey) : null;
                    } catch (readErr) {
                        // olvasási hibánál a régi viselkedés: visszaírjuk
                        stored = null;
                    }
                    // Ugyanaz a feltétel, mint a sync() szűrőjében (`!element.synced`),
                    // hogy pontosan azokat hagyjuk ki, amiket az úgyis elküld.
                    if (stored && !stored.synced) {
                        continue;
                    }
                    // JAVÍTÁS: write() helyett update()! A write() a worker
                    // _write_internal-jában FELTÉTLENÜL felülírja a rekord
                    // period és clientTime mezőjét a JELENLEGI window.period
                    // és window.ts.now() értékére (ez a "brand new event"
                    // eset kiszolgálására való). Itt viszont MÁR LÉTEZŐ,
                    // korábban (esetleg egy korábbi félidőben) létrehozott
                    // eseményeket küldünk vissza szinkronizálásra - ezeknek
                    // meg kell tartaniuk a SAJÁT eredeti period értéküket. A
                    // write() emiatt DC/RC után minden, a reconnectkor még
                    // "unsynced" eseményt átcímkézett a JELENLEGI periódusra,
                    // ami összekeverte az idővonal sorrendjét. Az update() a
                    // period-hoz nem nyúl; a `clientTime`-ot viszont (a
                    // dbmanager._update központi bélyegzése óta) frissíti - egy
                    // valódi visszaírásnál erre a verziókezelés miatt szükség
                    // van, ezért hagyjuk ki fentebb a feleslegeseket.
                    await window.dbmanager.update(cleanEvent, false);
                } catch (err) {
                    console.error("[SYNC] Error pushing event to server:", event, err);
                }
            }
            console.log("[SYNC] Push complete.");
        } else {
            console.log("[SYNC] No local changes to push.");
        }
    },

    socketDmrPush({commit, dispatch}, data) {
        if (data.action !== 'init' && (data.params || data.deleted)) {
            commit('UPDATE_TIMELINE_EVENT', {
                ...data,
                id: data.UUID,
                UUID: data.UUID
            });
            if (dmrPushDebounceTimer) {
                clearTimeout(dmrPushDebounceTimer);
            }
            dmrPushDebounceTimer = setTimeout(() => {
                dispatch('updateSortedTimeline');
                dispatch('recalculateRejecteds');

                // --- JAVÍTÁS: Eredmény újraszámolása a frissített lista alapján ---
                // Ha töröltünk/módosítottunk egy gólt, újra kell összegezni.
                // sync: false fontos, hogy a DMR ne próbálja visszaírni az adatbázisba a változást.
                dispatch('reCalculateScores', {totalGameTime: 0, sync: false});
            }, 250);
        }
    },

    async givePenalty({ dispatch, state, getters, commit, rootGetters }, { player, event }) {
        const targetPlayer = getters.getPlayerById(player.id);
        if (!targetPlayer) return;

        // --- JAVÍTÁS: RACE CONDITION VÉDELEM ---
        // Ha ennél a játékosnál már folyamatban van a büntetés kiosztása,
        // azonnal kilépünk, hogy ne kapjon duplán pirosat.
        if (processingPenalties.has(targetPlayer.id)) {
            console.log(`[Game] Penalty check skipped for player ${targetPlayer.id} - already processing.`);
            return;
        }

        // Zároljuk a játékost
        processingPenalties.add(targetPlayer.id);

        try {
            // Frissítjük a büntetéseket a számolás előtt
            await dispatch('restorePenalties');

            const currentOwnPenalties = targetPlayer.penalties ? targetPlayer.penalties.filter(p => !p.scapegoat).length : 0;
            const hasRed = targetPlayer.cards && targetPlayer.cards.red && targetPlayer.cards.red.value;

            // Ellenőrizzük, hogy van-e már ennek a játékosnak AUTO pirosa a timeline-on
            const alreadyHasAutoRed = state.timelineData.some(e =>
                !e.deleted &&
                e.action === 'piroslap' &&
                e.params &&
                e.params.selectedPlayer === targetPlayer.id &&
                e.params.auto === true
            );

            // Csak akkor adunk újat, ha még nincs neki (sem sima, sem auto)
            if (currentOwnPenalties >= (targetPlayer.maxPenalties || 3) && !hasRed && !alreadyHasAutoRed && event) {
                console.log(`[Game] Auto Red Card triggered for player ${targetPlayer.id}`);

                let redCardEvent = {
                    action: 'piroslap',
                    params: JSON.parse(JSON.stringify(event.params || {})),
                    gametime: event.gametime,
                    totalGameTime: event.totalGameTime,
                    clientTime: window.ts.now(),
                    isDraft: false
                };
                redCardEvent.params.auto = true;
                redCardEvent.params.selectedAction = { actionId: 'piroslap', actionName: 'Piros lap (auto)' };

                // Mivel ez auto lap, ne örökölje a "2perc" típusát, hanem legyen tiszta
                delete redCardEvent.params.selectedActionType;

                if (rootGetters['connection/isOffline']) {
                    redCardEvent.synced = false;
                    redCardEvent.syncDone = false;
                }

                await window.dbmanager.write(redCardEvent).then(async (result) => {
                    if(result) {
                        const record = await window.dbmanager.read(result);
                        if (record) {
                            if (!record.id && record.UUID) record.id = record.UUID;
                            commit('UPDATE_TIMELINE_EVENT', record);
                            dispatch('updateSortedTimeline');
                        }
                    }
                });
            }
        } catch (error) {
            console.error("[Game] Error in givePenalty:", error);
        } finally {
            // --- JAVÍTÁS: Zár feloldása ---
            // Akár sikerült, akár hiba történt, feloldjuk a játékost
            processingPenalties.delete(targetPlayer.id);
        }
    },

    async askTimeOut({state, getters, commit, dispatch, rootGetters}, {teamId}) {
        if (!getters.canTeamAskTimeOut(teamId)) return;

        eventBus.$emit(CONSTANTS.EVENTS.CLOCK.STOP);

        const team = teamId === state.teamA.team_id ? state.teamA : state.teamB;
        const teamKey = teamId === state.teamA.team_id ? 'teamA' : 'teamB';
        const currentTime = window.stopwatch.getTotalTimeAsString();

        let currentHalfTime = state.currentPeriod;
        if (state.currentPeriod === '2') currentHalfTime = '1';
        if (state.currentPeriod === '3') currentHalfTime = '2';

        const currentDirection = state.teamA.isActive ? 'left' : 'right';
        const newTotalUsed = team.totalUsedTimeOuts + 1;

        const newUUID = window.dbmanager.generateUUID();
        const payload = {
            id: newUUID,
            UUID: newUUID,
            action: 'timeOut',
            params: {
                isDraft: false,
                selectedTeam: teamId,
                selectedTeamId: teamKey,
                selectedAction: {actionId: 'timeOut', actionName: 'Időkérés'},
                usedTimeOuts: newTotalUsed,
                currentPeriod: currentHalfTime,
                gameTime: currentTime,
                teamScore: {
                    teamA: state.gamedata.team_a_score,
                    teamB: state.gamedata.team_b_score,
                },
                type: 'used',
                attackDirection: currentDirection,
                // JAVÍTÁS: a többi DMR-jóváhagyást igénylő akcióval (ld. ActionBar.vue
                // saveAction) ellentétben ez itt sosem állította be az approved/rejectReason
                // mezőket létrehozáskor, így egy MÉG EL NEM BÍRÁLT (se jóvá nem hagyott, se el
                // nem utasított) időkérés params.approved értéke undefined maradt - a
                // recalculateRejecteds viszont szigorúan `=== false`-ra szűr, ezért az ilyen,
                // still-pending időkérés sosem számított bele a beküldést blokkoló
                // rejectedCount-ba. Explicit false-ra állítva a jóváhagyásra váró állapot is
                // helyesen blokkolja a jegyzőkönyv beküldését, amíg a DMR el nem bírálja.
                approved: false,
                rejectReason: null,
                message: null
            },
            isDraft: false,
            gametime: window.stopwatch.getTime(),
            totalGameTime: window.stopwatch.getTotalTime(),
            period: state.currentPeriod,
            clientTime: window.ts.now()
        };

        if (rootGetters['connection/isOffline']) {
            payload.synced = false;
            payload.syncDone = false;
        }

        commit('UPDATE_TIMELINE_EVENT', payload);
        dispatch('updateSortedTimeline');
        dispatch('recalculateTimeOuts');

        try {
            const result = await window.dbmanager.write(payload);

            if (result) {
                const record = await window.dbmanager.read(result);
                if (record) {
                    commit('UPDATE_TIMELINE_EVENT', record);
                    dispatch('updateSortedTimeline');
                    eventBus.$emit(CONSTANTS.EVENTS.RECORD.CREATED, record);
                }
            }
        } catch (e) {
            console.error("Hiba az időkérés mentésekor, visszavonás...", e);
            commit('REMOVE_TIMELINE_EVENT', newUUID);
            dispatch('recalculateTimeOuts');
        }
    },

    updateSortedTimeline({state, commit}) {
        let sortedList = [];

        if (state.timelineData && state.timelineData.length) {
            // JAVÍTÁS (MEMÓRIA/CPU, 77. tétel): eddig itt is a TELJES idővonal
            // JSON-mélymásolata készült (150 eseménynél mentésenként ~15 MB
            // szemét). A lenti kód a másolaton csak a `params.teamScore`-t írja
            // át, ezért elég egy sekély másolat, amelynek SAJÁT `params`-a és
            // SAJÁT `teamScore`-ja van - a store `timelineData`-ja így továbbra
            // sem változik. A lista egyetlen olvasója a ReportScreen ->
            // TimelineList, amely csak olvassa.
            sortedList = state.timelineData.map(item => {
                const copy = Object.assign({}, item);
                if (item.params) {
                    copy.params = Object.assign({}, item.params);
                    copy.params.teamScore = Object.assign({}, item.params.teamScore || {});
                }
                return copy;
            });
            sortedList = sortedList.filter(item => item.deleted != 1 && item.deleted !== true);
            sortedList = sortedList.filter(item => (item.action !== 'timeOut' || (item.action === 'timeOut' && item.params && item.params.type === 'used')));
            sortedList = sortedList.filter(item => item.action !== null);

            // Deduplikáció UUID/id alapján — ha bármilyen okból duplikátum kerülne be
            const seen = new Set();
            sortedList = sortedList.filter(item => {
                const key = item.UUID || item.id;
                if (!key) return true; // kulcs nélküli elemeket megtartjuk
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });

            // JAVÍTÁS (TELJESÍTMÉNY): a `require()` és az `isStrand` kiszámítása
            // korábban a komparátor BELSEJÉBEN volt, tehát MINDEN egyes
            // összehasonlításnál kétszer futott le egy modul-feloldás. Egy
            // rendezés ~N*logN összehasonlítás, így 1000 eseménynél ez kb.
            // 20 000 felesleges modul-lookup EGYETLEN hívás alatt - és ez a
            // függvény minden rögzített eseménynél ÉS a 10 másodperces
            // resync-ciklusban is lefut. A kiemeléssel a viselkedés
            // változatlan, csak egyszer történik meg.
            const isStrand = state.gamedata && state.gamedata.rule === 'STRAND';
            const getChronoOrder = require('@/utils/periodOrder').getChronoOrder;

            sortedList.reverse().sort(function (a, b) {
                const pA = getChronoOrder(a.period, isStrand);
                const pB = getChronoOrder(b.period, isStrand);

                if (pA > pB) return 1;
                else if (pA === pB) {
                    const timeA = (a.params && a.params.gameTime) ? a.params.gameTime : '00:00';
                    const timeB = (b.params && b.params.gameTime) ? b.params.gameTime : '00:00';

                    if (timeA > timeB) return 1;
                    if (timeA === timeB && (a.date || a.clientTime || 0) > (b.date || b.clientTime || 0)) return 1;
                }
                return -1;
            });

            let allScoreTeamA = 0;
            let allScoreTeamB = 0;
            let firstPeriodScores = {teamA: 0, teamB: 0};
            let secondPeriodScores = {teamA: 0, teamB: 0};
            let finalPeriodScores = {teamA: 0, teamB: 0};

            sortedList.forEach(function (item) {
                let action = item.params && item.params.selectedAction && item.params.selectedAction.actionId ? item.params.selectedAction.actionId : undefined;
                if (!item.deleted && action && (CONSTANTS.ACTIONS.SINGLE_SCORE_ACTIONS.includes(action) || CONSTANTS.ACTIONS.DOUBLE_SCORE_ACTIONS.includes(action))) {
                    let increaseScoreBy = CONSTANTS.ACTIONS.DOUBLE_SCORE_ACTIONS.includes(action) ? 2 : CONSTANTS.ACTIONS.SINGLE_SCORE_ACTIONS.includes(action) ? 1 : 0;

                    if (item.params.selectedTeamId === 'teamA') {
                        allScoreTeamA += increaseScoreBy;
                        firstPeriodScores.teamA += parseInt(item.period) === 1 ? increaseScoreBy : 0;
                        secondPeriodScores.teamA += parseInt(item.period) === 3 ? increaseScoreBy : 0;
                        finalPeriodScores.teamA += parseInt(item.period) === 12 ? increaseScoreBy : 0;
                    } else if (item.params.selectedTeamId === 'teamB') {
                        allScoreTeamB += increaseScoreBy;
                        firstPeriodScores.teamB += parseInt(item.period) === 1 ? increaseScoreBy : 0;
                        secondPeriodScores.teamB += parseInt(item.period) === 3 ? increaseScoreBy : 0;
                        finalPeriodScores.teamB += parseInt(item.period) === 12 ? increaseScoreBy : 0;
                    }
                }

                if (item.params && !item.params.teamScore) item.params.teamScore = {};

                if (item.params && item.params.teamScore) {
                    if (state.gamedata.rule === 'STRAND') {
                        let cardPeriodScoreA = 0;
                        let cardPeriodScoreB = 0;
                        switch (parseInt(item.period)) {
                            case 1: cardPeriodScoreA = firstPeriodScores.teamA; cardPeriodScoreB = firstPeriodScores.teamB; break;
                            case 3: cardPeriodScoreA = secondPeriodScores.teamA; cardPeriodScoreB = secondPeriodScores.teamB; break;
                            case 12: cardPeriodScoreA = finalPeriodScores.teamA; cardPeriodScoreB = finalPeriodScores.teamB; break;
                        }
                        item.params.teamScore.teamA = cardPeriodScoreA;
                        item.params.teamScore.teamB = cardPeriodScoreB;
                    } else {
                        item.params.teamScore.teamA = allScoreTeamA;
                        item.params.teamScore.teamB = allScoreTeamB;
                    }
                }
            });
        }
        // JAVÍTÁS (MEMÓRIA/CPU, 77. tétel): a lista csak megjelenítésre szolgál
        // (senki nem írja), ezért befagyasztjuk - így a Vue nem teszi minden
        // mentésnél újra reaktívvá mind a ~150 elemet és azok `params`-át.
        // A lista cseréjét (új tömb) a Vue továbbra is észleli.
        sortedList.forEach(item => Object.freeze(item));
        commit('SET_SORTED_LIST', Object.freeze(sortedList));
    },

    changePeriod({dispatch, commit}, period) {
        commit('SET_PERIOD', period);
        dispatch('recalculateTimeOuts');
    },

    recalculateTimeOuts({state, commit}) {
        let maxTimeOuts = typeof state.gamedata.maxTimeOuts === 'undefined' ? 3 : state.gamedata.maxTimeOuts;
        let periodLength = parseInt(state.gamedata.period_length || 30);
        let fiveMinutesBeforeEnd = (periodLength * 2) - 5;
        let isBeach = state.gamedata.rule === 'STRAND';

        let currentTime = 0;
        if (window.stopwatch) currentTime = window.stopwatch.getTotalTime();
        let currentMinute = Math.floor(currentTime / 60);

        // JAVÍTÁS: eddig csak a "2. félidő utolsó 5 perce" (period==='3') és a
        // "meccs teljesen vége" (period==='13') eset volt lefedve - a köztes
        // "Rendes játékidő vége" (period==='4') és minden azutáni periódus
        // (hosszabbítás, 7m dobások) kimaradt, ezért ott a fel nem használt
        // időkérések tévesen "elérhetőnek" (üres zöld) látszottak "elveszett"
        // (piros) helyett. A gamedata.periods saját, hiteles "order" mezőjét
        // használjuk annak eldöntésére, hogy a jelenlegi periódus a rendes
        // játékidő (3. periódus) UTÁN van-e.
        const currentPeriodDef = (state.gamedata.periods || []).find(p => p.value === state.currentPeriod);
        const period3Def = (state.gamedata.periods || []).find(p => p.value === '3');
        const isPastRegulation = !!(currentPeriodDef && period3Def && parseInt(currentPeriodDef.order) > parseInt(period3Def.order));

        const isGameFinished = state.gameFinished || state.currentPeriod === '13' || isPastRegulation;

        const buildList = (teamId) => {
            const teamKey = teamId === state.teamA.team_id ? 'teamA' : 'teamB';
            let slots = [];
            let currentId = 1;

            const usedEvents = state.timelineData.filter(e =>
                // JAVÍTÁS: l. a fájl elején lévő azonos javítás - a DMR
                // törlése boolean `true`-t ír, nem numerikus `1`-et.
                e.deleted !== 1 && e.deleted !== true &&
                e.params &&
                e.params.selectedTeamId === teamKey &&
                e.params.selectedAction &&
                e.params.selectedAction.actionId === 'timeOut' &&
                e.params.type === 'used'
            ).sort((a, b) => (a.gametime || 0) - (b.gametime || 0));

            const p1Used = usedEvents.filter(e => e.period === '1');
            const p2Used = usedEvents.filter(e => e.period !== '1');

            for (let i = 0; i < p1Used.length; i++) {
                if (slots.length < maxTimeOuts) {
                    slots.push({ id: currentId++, used: true, lost: false });
                }
            }

            if (!isBeach) {
                if (state.currentPeriod !== '1' && p1Used.length === 0) {
                    if (slots.length < maxTimeOuts) {
                        slots.push({ id: currentId++, used: false, lost: true });
                    }
                }
            }

            // JAVÍTÁS: a 2. félidőben felhasznált időkéréseket az 5 perces határhoz
            // képest ELŐTTE/UTÁNA csoportokra bontjuk, hogy az "elveszett" (piros)
            // jelölés a HATÁR pillanatában, a kronológiailag helyes POZÍCIÓBA kerüljön
            // be - ne pedig mindig az addig felhasznált időkérések MÖGÉ. Enélkül egy,
            // a határ UTÁN kikért időkérés a slots tömb VÉGÉRE kerülne, és eltolná a
            // már megjelölt piros slotot, holott annak a helyén (a levágás pillanatában)
            // kellene maradnia.
            const cutoffSeconds = fiveMinutesBeforeEnd * 60;
            const p2UsedBeforeCutoff = p2Used.filter(e => (e.totalGameTime || 0) < cutoffSeconds);
            const p2UsedAfterCutoff = p2Used.filter(e => (e.totalGameTime || 0) >= cutoffSeconds);

            for (let i = 0; i < p2UsedBeforeCutoff.length; i++) {
                if (slots.length < maxTimeOuts) {
                    slots.push({ id: currentId++, used: true, lost: false });
                }
            }

            // JAVÍTÁS: korábban ez a feltétel KIZÁRÓLAG akkor engedte lefutni az
            // "elveszett slot" számítást, ha a currentPeriod ÉPP '3' volt abban a
            // pillanatban, amikor ez a függvény lefutott. Ha a periódus időközben
            // már továbblépett (pl. "Rendes játékidő vége"-re), ez a blokk
            // TELJESEN kimaradt - és egy, az 5 perces határ UTÁN kikért időkérés
            // (ami a valós, IHF-szabály szerinti 3. slot-ba tartozik) tévesen a 2.
            // slot-ba került, a 3. slot pedig utólag, a lenti "isGameFinished"
            // ágban tévesen "elveszettként" (piros) lett megjelölve - felcserélve
            // a 2. és 3. slot valós állapotát. A helyes eredmény ugyanaz kell
            // legyen, akár PONT az 5 perces határnál, akár BÁRMIKOR később (akár a
            // meccs vége után) fut le ez a számítás, hiszen a bemenete (a határ
            // ELŐTTI felhasználások száma) attól nem változik - ezért a
            // `isPastRegulation`-t is elfogadjuk a szigorú `currentPeriod === '3'`
            // mellett/helyett.
            if (!isBeach && (isPastRegulation || (state.currentPeriod === '3' && currentMinute >= fiveMinutesBeforeEnd))) {
                // A "hány slot vész el" számítást KIZÁRÓLAG az 5 perces határ ELŐTTI
                // felhasználásokra szabad alapozni. Ha az összes p2Used-et (vagyis a
                // határ UTÁN kikért időkéréseket is) használnánk, akkor egy a határ után
                // kikért időkérés visszamenőleg "feltámasztaná" (zölddé tenné) a már
                // jogosan elvesztettként megjelölt (piros) slotot.
                const filledBeforeCutoff = (p1Used.length === 0 ? 1 : p1Used.length) + p2UsedBeforeCutoff.length;
                const remaining = maxTimeOuts - filledBeforeCutoff;

                if (remaining > 1) {
                    const toBurn = remaining - 1;
                    for(let k=0; k<toBurn; k++) {
                        if (slots.length < maxTimeOuts) {
                            slots.push({ id: currentId++, used: false, lost: true });
                        }
                    }
                }
            }

            for (let i = 0; i < p2UsedAfterCutoff.length; i++) {
                if (slots.length < maxTimeOuts) {
                    slots.push({ id: currentId++, used: true, lost: false });
                }
            }

            if (isGameFinished) {
                while(slots.length < maxTimeOuts) {
                    slots.push({ id: currentId++, used: false, lost: true });
                }
            }

            while (slots.length < maxTimeOuts) {
                slots.push({ id: currentId++, used: false, lost: false });
            }

            return { timeOuts: slots, totalUsed: usedEvents.length };
        };

        if (state.gamedata && state.timelineData) {
            const teamAData = buildList(state.teamA.team_id);
            const teamBData = buildList(state.teamB.team_id);

            commit('SET_TEAM_TIMEOUTS', { team: 'A', timeOuts: teamAData.timeOuts, totalUsed: teamAData.totalUsed });
            commit('SET_TEAM_TIMEOUTS', { team: 'B', timeOuts: teamBData.timeOuts, totalUsed: teamBData.totalUsed });
        } else {
            let emptyList = [];
            for(let i=1; i<=maxTimeOuts; i++) emptyList.push({id: i, used: false, lost: false});
            commit('SET_TEAM_TIMEOUTS', { team: 'A', timeOuts: emptyList, totalUsed: 0 });
            commit('SET_TEAM_TIMEOUTS', { team: 'B', timeOuts: emptyList, totalUsed: 0 });
        }
    },

    saveStrandGoalkeepers({commit, state, dispatch}, {goalkeeperAId, goalkeeperBId}) {
        dispatch('updateGoalkeeper', {team: 'A', playerId: goalkeeperAId || null});
        dispatch('updateGoalkeeper', {team: 'B', playerId: goalkeeperBId || null});

        if (state.teamA.isAttacking) {
            commit('SET_STRAND_SELECTION_FLAG', {team: 'B', value: true});
        } else {
            commit('SET_STRAND_SELECTION_FLAG', {team: 'A', value: true});
        }
    },

    updateGoalkeeper({commit, state, rootGetters}, {team, playerId}) {
        // JAVÍTÁS: ID kiszedése objektumból (biztos, ami biztos)
        let cleanId = playerId;
        if (playerId && typeof playerId === 'object') {
            cleanId = playerId.id || playerId.value;
            if (cleanId && typeof cleanId === 'object') {
                cleanId = cleanId.id;
            }
        }

        commit('SET_GOALKEEPER', {team, playerId: cleanId});
        if (cleanId) {
            commit('SET_STRAND_SELECTION_FLAG', {team, value: true});
        }

        const teamKey = team === 'A' ? 'team_a_actual_goalkeeper' : 'team_b_actual_goalkeeper';
        const value = cleanId ? {id: cleanId} : {id: null};

        let writeParams = {
            action: 'gamedatachange',
            params: {
                name: teamKey,
                value: value,
                gametime: window.stopwatch.getTime(),
                totalGameTime: window.stopwatch.getTotalTime()
            }
        };

        if (rootGetters['connection/isOffline']) {
            writeParams.synced = false;
            writeParams.syncDone = false;
        }

        window.dbmanager.write(writeParams).then(() => {
            console.log(`Vuex: Kapus mentve (${team}):`, cleanId);
        }).catch(err => {
            console.error("Vuex: Hiba a kapus mentésekor", err);
        });
    },

    selectPlayer({commit, getters}, playerId) {
        // Ha nincs kiválasztott játékos (deselection), mindent engedélyezünk
        if (!playerId) {
            const events = ['SHOTS', 'PENALTYSHOTS', 'LOSTBALLS', 'COURT', 'GATE', 'PENALTIES', 'CARDS'];
            events.forEach(evt => {
                eventBus.$emit(CONSTANTS.ACTIONS.DISABLE[evt], {component: 'player', value: false});
            });
            return;
        }

        const perms = getters.getPlayerPermissions(playerId);
        if (!perms) {
            console.error("Nem található játékos vagy hiba a jogosultságok számításában", playerId);
            return;
        }

        // Ha a játékos nem cselekedhet (pl. védő, vagy PIROS LAPJA VAN), akkor ez true lesz
        const disableAction = !perms.canPerformAction;

        // 1. Sima lövések letiltása
        eventBus.$emit(CONSTANTS.ACTIONS.DISABLE.SHOTS, {component: 'player', value: disableAction});

        // --- ÚJ RÉSZEK KEZDETE ---

        // 2. 7 méteresek letiltása (ActionPenaltyShots.vue)
        eventBus.$emit(CONSTANTS.ACTIONS.DISABLE.PENALTYSHOTS, {component: 'player', value: disableAction});

        // 3. Eladott labda / Technikai hiba letiltása (ActionLostball.vue)
        eventBus.$emit(CONSTANTS.ACTIONS.DISABLE.LOSTBALLS, {component: 'player', value: disableAction});

        // 4. Pálya és Kapu letiltása (ne lehessen zónát választani)
        eventBus.$emit(CONSTANTS.ACTIONS.DISABLE.COURT, {component: 'player', value: disableAction});
        eventBus.$emit(CONSTANTS.ACTIONS.DISABLE.GATE, {component: 'player', value: disableAction});

        // --- ÚJ RÉSZEK VÉGE ---

        // 5. Lapok és kiállítások speciális kezelése (ez már benne volt, de itt hagyjuk)
        eventBus.$emit(CONSTANTS.ACTIONS.DISABLE.PENALTIES, {
            component: 'player',
            value: {
                "2": !perms.canReceive2Min,
                "4": !perms.canReceive4Min,
                "2perc": !perms.canReceive2Min,
                "4perc": !perms.canReceive4Min,
                "sargalap": !perms.canReceiveYellow,
                "piroslap": !perms.canReceiveRed,
                "keklap": !perms.canReceiveBlue // Ez engedve lesz, ha van pirosa!
            }
        });
    },

    giveYellowCard({commit, getters}, payload) {
        let player = payload;
        if (payload && payload.params && payload.params.selectedPlayer) {
            player = getters.getPlayerById(payload.params.selectedPlayer);
        }
        if (!player) return;

        if (player.cards && player.cards.yellow && player.cards.yellow.value) {
            return;
        }

        let type = player.official ? 'officials' : 'players';
        let teamKey = (player.team === 'A' || player.team === 'teamA') ? 'teamA' : 'teamB';
        commit('UPDATE_YELLOW_COUNT', {type, team: teamKey, change: 1});
    },

    removeYellowCard({commit, getters}, payload) {
        let player = payload;
        if (payload && payload.params && payload.params.selectedPlayer) {
            player = getters.getPlayerById(payload.params.selectedPlayer);
        }
        if (!player) return;

        if (!player.cards || !player.cards.yellow || !player.cards.yellow.value) {
            return;
        }

        let type = player.official ? 'officials' : 'players';
        let teamKey = (player.team === 'A' || player.team === 'teamA') ? 'teamA' : 'teamB';
        commit('UPDATE_YELLOW_COUNT', {type, team: teamKey, change: -1});
    },

    restoreCards({state}) {
        // JAVÍTÁS (MEMÓRIA/CPU, 77. tétel): eddig itt MINDEN játékos minden
        // lapját előbb `false`-ra állítottuk, majd az események alapján újra
        // `true`-ra. A reaktív true -> false -> true váltás miatt minden hívásnál
        // (minden gól/lap/kiállítás mentésekor) az összes játékos-kártya
        // újrarajzolódott, akkor is, ha semmi nem változott (150 eseménynél
        // ~120 ms). Most a végállapotot előbb reaktivitás nélkül számoljuk ki,
        // pontosan ugyanazzal a logikával, és csak az eltérő mezőket írjuk át.
        const allLists = [
            state.gamedata.team_a_players, state.gamedata.team_b_players,
            state.gamedata.team_a_officials, state.gamedata.team_b_officials
        ];
        const playerIndex = buildPlayerIndex(state.gamedata);
        // játékos -> {yellow, red, redGiven, blue, blueGiven}; a `...Given`
        // csak akkor kerül bele, ha az adott lap ki van adva (mint eddig).
        const desired = new Map();

        let localCounts = {
            players: { teamA: 0, teamB: 0 },
            officials: { teamA: 0, teamB: 0 }
        };

        if (state.timelineData && state.timelineData.length) {
            const sortedEvents = [...state.timelineData].sort((a, b) => (a.date || a.clientTime || 0) - (b.date || b.clientTime || 0));

            sortedEvents.forEach(event => {
                if (event.deleted || event.isDraft) return;

                const action = event.params && event.params.selectedAction ? event.params.selectedAction.actionId : null;
                const playerId = event.params ? event.params.selectedPlayer : null;

                if (playerId) {
                    const player = playerIndex.get(playerId);

                    if (player && player.cards) {
                        const eventTime = event.totalGameTime || 0;
                        let d = desired.get(player);
                        if (!d) {
                            d = {yellow: false, red: false, blue: false};
                            desired.set(player, d);
                        }

                        if (action === 'sargalap') {
                            d.yellow = true;

                            let type = player.official ? 'officials' : 'players';
                            let teamKey = (player.team === 'A' || player.team === 'teamA') ? 'teamA' : 'teamB';

                            if (localCounts[type] && localCounts[type][teamKey] !== undefined) {
                                localCounts[type][teamKey]++;
                            }

                        } else if (action === 'piroslap') {
                            d.red = true;
                            d.redGiven = eventTime;
                        } else if (action === 'keklap') {
                            d.blue = true;
                            d.blueGiven = eventTime;
                        }
                    }
                }
            });
        }

        // Alkalmazás: csak ott írunk, ahol az érték ténylegesen eltér.
        allLists.forEach(list => {
            if (!list) return;
            list.forEach(player => {
                if (!player.cards) return;
                const d = desired.get(player) || {yellow: false, red: false, blue: false};
                const c = player.cards;
                if (c.yellow && c.yellow.value !== d.yellow) c.yellow.value = d.yellow;
                if (c.red) {
                    if (c.red.value !== d.red) c.red.value = d.red;
                    if (d.red && c.red.given !== d.redGiven) c.red.given = d.redGiven;
                }
                if (c.blue) {
                    if (c.blue.value !== d.blue) c.blue.value = d.blue;
                    if (d.blue && c.blue.given !== d.blueGiven) c.blue.given = d.blueGiven;
                }
            });
        });

        if (state.yellows) {
            var rule = state.gamedata && state.gamedata.rule ? state.gamedata.rule : '';
            var playerLimit = rule === 'STRAND' ? Infinity : 3;
            var officialLimit = rule === 'STRAND' ? Infinity : 1;

            if (state.yellows.players) {
                state.yellows.players.teamA.given = localCounts.players.teamA;
                state.yellows.players.teamA.max = playerLimit;

                state.yellows.players.teamB.given = localCounts.players.teamB;
                state.yellows.players.teamB.max = playerLimit;
            }
            if (state.yellows.officials) {
                state.yellows.officials.teamA.given = localCounts.officials.teamA;
                state.yellows.officials.teamA.max = officialLimit;

                state.yellows.officials.teamB.given = localCounts.officials.teamB;
                state.yellows.officials.teamB.max = officialLimit;
            }
        }
    },

    restorePenalties({state}) {
        // JAVÍTÁS (MEMÓRIA/CPU, 77. tétel): eddig MINDEN játékos kiállítás-
        // listáját üres tömbre cseréltük, majd újratöltöttük. Ez minden
        // hívásnál (minden gól/lap/kiállítás mentésekor, 2 percnél kétszer is)
        // az összes játékos kártyáját és a mélyen figyelt `player.penalties`
        // watchert újrafuttatta (150 eseménynél ~160 ms). Most ugyanazzal az
        // algoritmussal IDEIGLENES tömbökbe számolunk, és egy játékos listáját
        // csak akkor cseréljük le, ha a kiállításai tartalmilag (azonosító,
        // kezdet, vég, bűnbak) eltérnek. Ha nem térnek el, a meglévő objektumok
        // maradnak - az `isActive`/`finished` állapotukat a Player.vue /
        // PlayerPenalty.vue `checkWatch()` úgyis folyamatosan az órához igazítja.
        const allLists = [
            state.gamedata.team_a_players, state.gamedata.team_b_players,
            state.gamedata.team_a_officials, state.gamedata.team_b_officials
        ];
        const playerIndex = buildPlayerIndex(state.gamedata);
        const computed = new Map(); // játékos -> új kiállítás-lista
        const getPlayerById = (id) => playerIndex.get(id);

        if (state.timelineData) {
            const sortedEvents = [...state.timelineData].sort((a, b) => (a.clientTime || 0) - (b.clientTime || 0));
            const processedEventIds = new Set();

            sortedEvents.forEach(event => {
                if (event.deleted) return;
                const eventId = event.id || event.UUID;
                if (eventId && processedEventIds.has(eventId)) return;

                const action = event.params && event.params.selectedAction ? event.params.selectedAction.actionId : null;
                const playerId = event.params ? event.params.selectedPlayer : null;

                // --- JAVÍTÁS: Megnézzük, hogy auto piros-e ---
                const isAutoRed = (action === 'piroslap' && event.params && event.params.auto);

                // A feltételbe beletesszük: piroslap csak akkor, ha NEM auto!
                if (playerId && (action === '2perc' || action === '4perc' || (action === 'piroslap' && !isAutoRed))) {
                    const player = getPlayerById(playerId);
                    if (player) {
                        if (!computed.has(player)) computed.set(player, []);
                        const penalties = computed.get(player);
                        if (eventId) processedEventIds.add(eventId);

                        // 4 perc esetén 2 db 2 perces büntetés
                        const count = (action === '4perc') ? 2 : 1;
                        const duration = (action === '4perc') ? 240 : 120; // Itt a logikád szerint maradhat 240 vagy 120, de a loop miatt a 120 a jobb, ha 2 db-ot raksz

                        let startTime = event.totalGameTime || event.gametime || 0;
                        const isScapegoat = event.params.selectedScapegoat ? true : false;

                        for(let i=0; i<count; i++) {
                            // ... (a belső logika változatlan) ...
                            let newPenalty = {
                                id: (eventId || Date.now()) + '_' + i,
                                time: { start: startTime, stop: startTime + 120 }, // Fix 120 mp
                                isActive: false,
                                finished: false,
                                scapegoat: isScapegoat
                            };
                            penalties.sort((a, b) => a.time.start - b.time.start);
                            for (let existing of penalties) {
                                if (newPenalty.time.start >= existing.time.start && newPenalty.time.start < existing.time.stop) {
                                    newPenalty.time.start = existing.time.stop;
                                    newPenalty.time.stop = newPenalty.time.start + 120;
                                }
                            }
                            penalties.push(newPenalty);
                        }
                    }
                }
            });
        }

        // Alkalmazás: csak az eltérő listákat cseréljük (l. fent).
        allLists.forEach(list => {
            if (!list) return;
            list.forEach(player => {
                const next = computed.get(player) || [];
                if (penaltySignature(player.penalties) !== penaltySignature(next)) {
                    player.penalties = next;
                }
            });
        });
    },

    // --------------------------------------------------------------------------------
    // JAVÍTÁS: ID vagy UUID használata + Hibakezelés a read() hívásnál
    // --------------------------------------------------------------------------------
    reCalculateScores({state, commit, dispatch}, {totalGameTime = 0, sync = true} = {}) {
        // JAVÍTÁS (MEMÓRIA/CPU, 77. tétel - 32021458074): ez a függvény minden
        // gól/lap/kiállítás mentésekor lefut, és eddig a TELJES idővonalat
        // JSON-nal lemásolta, majd a másolattal cserélte le a store-ét. 150
        // eseménynél ez mentésenként ~27 MB szemetet termelt, a Vue-nak minden
        // eseményt újra reaktívvá kellett tennie, és minden tőle függő komponens
        // újrarajzolódott - egy 3 GB-os iPadon ez már érezhető memórianyomás.
        // Most ugyanazokat az objektumokat rendezzük (a szűrés és a rendezés
        // változatlan), és a `teamScore`-t csak ott cseréljük, ahol az állás
        // tényleg változott. Az eredmény ugyanaz, a másolás elmarad.
        let _localTimeLine = [];
        if (state.timelineData) {
            _localTimeLine = state.timelineData.slice();
        }

        _localTimeLine = _localTimeLine.filter(function (item) {
            return item.deleted != 1 && item.deleted !== true && item.params;
        });

        _localTimeLine.sort(function (a, b) {
            var aTime = (a.params && a.params.gameTime) || '';
            var bTime = (b.params && b.params.gameTime) || '';
            if (aTime > bTime) {
                return 1;
            }
            if (aTime === bTime && (a.date || a.clientTime || 0) > (b.date || b.clientTime || 0)) {
                return 1;
            }
            return -1;
        });

        let allNumOfScoresTeamA = 0;
        let allNumOfScoresTeamB = 0;
        const scoreUpdates = [];

        if (_localTimeLine.length) {
            _localTimeLine.forEach(function (obj) {
                if (obj.deleted != 1 && obj.deleted !== true && obj.params.selectedAction && (CONSTANTS.ACTIONS.SINGLE_SCORE_ACTIONS.includes(obj.params.selectedAction.actionId) || CONSTANTS.ACTIONS.DOUBLE_SCORE_ACTIONS.includes(obj.params.selectedAction.actionId))) {
                    let increaseScoreBy = CONSTANTS.ACTIONS.DOUBLE_SCORE_ACTIONS.includes(obj.params.selectedAction.actionId) ? 2 : 1;
                    if (obj.params.selectedTeamId === 'teamA') {
                        allNumOfScoresTeamA += increaseScoreBy;
                    } else if (obj.params.selectedTeamId === 'teamB') {
                        allNumOfScoresTeamB += increaseScoreBy;
                    }
                }

                const newScore = {
                    teamA: allNumOfScoresTeamA,
                    teamB: allNumOfScoresTeamB,
                };
                const currentScore = obj.params.teamScore;
                if (!currentScore || currentScore.teamA !== newScore.teamA || currentScore.teamB !== newScore.teamB) {
                    scoreUpdates.push({item: obj, score: newScore});
                }

                // FONTOS: Ha nincs ID (mert offline jött létre), használjuk az UUID-t!
                let recordId = obj.id || obj.UUID;

                // JAVÍTÁS: l. a fájl elején lévő azonos javítás - a DMR
                // törlése boolean `true`-t ír, nem numerikus `1`-et.
                if (recordId && obj.deleted !== 1 && obj.deleted !== true && obj.totalGameTime > totalGameTime && sync) {
                    window.dbmanager.read(recordId).then((record) => {
                        if (record && record.params) {
                            // JAVÍTÁS (ÍRÁS-VIHAR, 32021440528): csak akkor írunk,
                            // ha az állás TÉNYLEG változott. Korábban minden
                            // eseményt feltétel nélkül visszaírtunk - az
                            // `update(..., false)` viszont mindegyiknek új
                            // `clientTime`-ot ad, vagyis új, szinkronizálandó
                            // verziót csinál belőle. Egy egyszerű törlés (a
                            // TimelineList.vue `deleteItem` paraméter nélkül,
                            // tehát `sync: true`-val hívja ezt) így a TELJES
                            // eseménylistát egyszerre "szinkronizálatlanná"
                            // tette: élesben egy technikai hiba törlése után
                            // 150 ms-mal mind a 120 esemény új verziót kapott,
                            // és innen indult a 24 perces írás-vihar.
                            // Egy technikai hiba törlése így semmit nem ír át,
                            // egy gól törlése pedig csak az utána következő,
                            // valóban módosuló eseményeket.
                            const oldScore = record.params.teamScore;
                            const changed = !oldScore
                                || oldScore.teamA !== newScore.teamA
                                || oldScore.teamB !== newScore.teamB;
                            if (changed) {
                                record.params.teamScore = newScore;
                                window.dbmanager.update(record, false);
                            }
                        }
                    }).catch(err => {
                        // Elkapjuk a hibát, hogy ne fagyjon le az alkalmazás
                        // Ez a "Failed to execute 'get'" hibát elnyeli
                        console.warn(`Figyelmeztetés: Nem sikerült frissíteni a rekordot (reCalculateScores). Key: ${recordId}`, err);
                    });
                }
            });
        }

        commit('APPLY_TIMELINE_SCORES', {timeline: _localTimeLine, scoreUpdates});
        commit('UPDATE_SCORE', {team: 'A', score: allNumOfScoresTeamA});
        commit('UPDATE_SCORE', {team: 'B', score: allNumOfScoresTeamB});
        commit('UPDATE_GAMEDATA_SCORES', {teamA: allNumOfScoresTeamA, teamB: allNumOfScoresTeamB});

        dispatch('restoreCards');
        dispatch('restorePenalties');
        dispatch('recalculateTimeOuts');
        dispatch('updateSortedTimeline');
    },

    recalculateDrafts({state, commit}) {
        if (!state.timelineData) return;
        const count = state.timelineData.filter((event) => event.isDraft && !event.deleted).length;
        commit('SET_DRAFT_COUNT', count);
    },

    recalculateRejecteds({state, commit}) {
        if (!state.gamedata || !state.timelineData) return;
        if (!state.gamedata.dmr_enabled) {
            commit('SET_REJECTED_COUNT', 0);
            return;
        }
        const count = state.timelineData.filter((event) =>
            [...CONSTANTS.DMR.DMR_DRAFTS, ...CONSTANTS.DMR.APPROVE_REQUIRED].includes(event.action) &&
            !event.isDraft &&
            !event.deleted &&
            event.params &&
            event.params.approved === false
        ).length;
        commit('SET_REJECTED_COUNT', count);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
